// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { filterRows, sumBy, total } from '../data';
import { catInfo, jurisName, FATES, CATEGORIES } from '../domain';
import { renderSankey, type SankeyNodeIn, type SankeyLinkIn } from '../components/sankey';
import { attachSvgZoom } from '../components/svgZoom';
import { legendHtml } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatTonnes, fyShort } from '../utils/format';

export function renderFlow(root: HTMLElement, ctx: ViewContext): void {
  const d = ctx.data;
  let juris = 'ALL';
  let year = d.latestYear;
  let selected: string | null = null;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Where it goes: material → fate</h1>
      <p class="view-sub">Each band flows from a material on the left to its ${glossaryTerm('fate', 'fate')} on the right — ${glossaryTerm('recycling', 'recycled')},
        ${glossaryTerm('energy-recovery', 'energy')}, ${glossaryTerm('reuse', 'reused')} or sent to ${glossaryTerm('disposal', 'landfill')}. Band width is
        tonnes. Hover a band for the split; click a material to open its detail.</p>
    </div>
    <div class="controls">
      <span class="field-label">Area</span>
      <select data-role="juris">
        <option value="ALL">All of Australia</option>
        ${d.jurisCodes.map((c) => `<option value="${c}">${escapeHtml(jurisName(c))}</option>`).join('')}
      </select>
      <span class="field-label">Year</span>
      <select data-role="year">
        ${[...d.years].reverse().map((yy) => `<option value="${yy}"${yy === year ? ' selected' : ''}>${escapeHtml(fyShort(yy))}</option>`).join('')}
      </select>
      <button class="chip" data-role="clear" hidden>Clear selection ✕</button>
    </div>
    <div class="panel">
      <div class="panel-title" data-role="title"></div>
      <div class="panel-sub" data-role="sub"></div>
      <div class="treemap-frame" data-role="frame"></div>
      <div data-role="legend"></div>
    </div>
  `;

  const frame = root.querySelector('[data-role="frame"]') as HTMLElement;
  const titleEl = root.querySelector('[data-role="title"]') as HTMLElement;
  const subEl = root.querySelector('[data-role="sub"]') as HTMLElement;
  const legendEl = root.querySelector('[data-role="legend"]') as HTMLElement;
  const clearBtn = root.querySelector('[data-role="clear"]') as HTMLButtonElement;
  let zoom: { destroy(): void } | null = null;

  function build(): void {
    const rows = filterRows(d.rows, { yr: year, j: juris === 'ALL' ? undefined : juris });
    const totalT = total(rows);
    // sources: materials with tonnes, canonical order, only non-zero
    const matTotals = new Map<string, number>();
    for (const c of CATEGORIES) matTotals.set(c.key, total(rows.filter((r) => r.c === c.key)));
    const usedCats = CATEGORIES.filter((c) => (matTotals.get(c.key) || 0) > 0);
    const sources: SankeyNodeIn[] = usedCats.map((c) => ({ key: c.key, label: c.short, colour: c.colour, value: matTotals.get(c.key) || 0 }));

    const fateTotals = sumBy(rows, (r) => r.f);
    const usedFates = FATES.filter((f) => (fateTotals.get(f.key) || 0) > 0);
    const targets: SankeyNodeIn[] = usedFates.map((f) => ({ key: f.key, label: f.label, colour: f.colour, value: fateTotals.get(f.key) || 0 }));

    const sIdx = new Map(sources.map((s, i) => [s.key, i]));
    const tIdx = new Map(targets.map((t, i) => [t.key, i]));
    const links: SankeyLinkIn[] = [];
    for (const r of rows) {
      if (r.t <= 0) continue;
      const s = sIdx.get(r.c); const t = tIdx.get(r.f);
      if (s == null || t == null) continue;
      links.push({ s, t, value: r.t });
    }

    titleEl.textContent = `${juris === 'ALL' ? 'Australia' : jurisName(juris)} — ${formatTonnes(totalT)} of waste, ${fyShort(year)}`;
    subEl.textContent = selected ? `Highlighting ${catInfo(selected).short}. Click it again or Clear to reset.` : 'Thicker bands are bigger flows. Plastics runs almost entirely into landfill; metals and building rubble run into recycling.';
    clearBtn.hidden = !selected;

    zoom?.destroy();
    frame.innerHTML = renderSankey(sources, targets, links, {
      width: 780, height: Math.max(360, sources.length * 34 + 40), fmt: formatTonnes, sourceClickAttr: 'open-material', highlightSource: selected, scroll: false,
    });
    const svg = frame.querySelector('svg') as SVGSVGElement;
    zoom = attachSvgZoom(svg, { maxScale: 6 });
    svg.querySelectorAll('[data-open-material]').forEach((el) =>
      el.addEventListener('click', () => {
        const key = el.getAttribute('data-open-material') as string;
        selected = selected === key ? null : key;
        build();
        ctx.openMaterial(key);
      }),
    );
    // clicking a link highlights its source
    svg.querySelectorAll('.sankey-link').forEach((el) =>
      el.addEventListener('click', () => {
        const key = el.getAttribute('data-src') as string;
        selected = selected === key ? null : key;
        build();
      }),
    );

    legendEl.innerHTML = legendHtml(usedFates.map((f) => ({ colour: f.colour, label: f.label })));
  }

  root.querySelector('[data-role="juris"]')?.addEventListener('change', (e) => { juris = (e.target as HTMLSelectElement).value; selected = null; build(); });
  root.querySelector('[data-role="year"]')?.addEventListener('change', (e) => { year = (e.target as HTMLSelectElement).value; selected = null; build(); });
  clearBtn.addEventListener('click', () => { selected = null; build(); });

  build();
}
